import { useEffect, useState } from 'react';
import { cn } from '~/lib/utils';
import styles from './SlideBrowser.module.css';

export interface SlidePair {
  title: string;
  subtitle: string;
}

export interface ProblemSectionSlideBrowserProps {
  className?: string;
  pairs?: SlidePair[];
  pairIndex?: number;
  dissolving?: boolean;
  onTypingComplete?: () => void;
  titlePlaceholder?: string;
  subtitlePlaceholder?: string;
}

export const DEFAULT_SLIDE_PAIRS: SlidePair[] = [
  { title: 'Q4 Roadmap', subtitle: 'What we are shipping next' },
  { title: 'Big Announcement', subtitle: 'A presentation by Sarah' },
  { title: 'Team Update', subtitle: 'Updates from the team' },
];

type Phase =
  | 'idle'
  | 'typingTitle'
  | 'pauseAfterTitle'
  | 'typingSubtitle'
  | 'complete';

const TYPE_SPEED = 180;
const PAUSE_AFTER_TITLE_TYPED = 600;
const INITIAL_DELAY = 600;

const useForwardTypewriter = (
  pair: SlidePair | undefined,
  resetKey: number,
  onTypingComplete?: () => void,
) => {
  const [titleText, setTitleText] = useState('');
  const [subtitleText, setSubtitleText] = useState('');
  const [phase, setPhase] = useState<Phase>('idle');

  useEffect(() => {
    setTitleText('');
    setSubtitleText('');
    setPhase('idle');
    if (!pair) return;
    const timer = setTimeout(() => setPhase('typingTitle'), INITIAL_DELAY);
    return () => clearTimeout(timer);
  }, [resetKey, pair]);

  useEffect(() => {
    if (!pair || phase === 'idle' || phase === 'complete') return;

    if (phase === 'typingTitle') {
      if (titleText.length < pair.title.length) {
        const timer = setTimeout(() => {
          setTitleText(pair.title.slice(0, titleText.length + 1));
        }, TYPE_SPEED);
        return () => clearTimeout(timer);
      }
      setPhase('pauseAfterTitle');
      return;
    }

    if (phase === 'pauseAfterTitle') {
      const timer = setTimeout(() => setPhase('typingSubtitle'), PAUSE_AFTER_TITLE_TYPED);
      return () => clearTimeout(timer);
    }

    if (phase === 'typingSubtitle') {
      if (subtitleText.length < pair.subtitle.length) {
        const timer = setTimeout(() => {
          setSubtitleText(pair.subtitle.slice(0, subtitleText.length + 1));
        }, TYPE_SPEED);
        return () => clearTimeout(timer);
      }
      setPhase('complete');
      return;
    }
  }, [phase, titleText, subtitleText, pair]);

  useEffect(() => {
    if (phase === 'complete') {
      onTypingComplete?.();
    }
  }, [phase, onTypingComplete]);

  return {
    titleText,
    subtitleText,
    titleActive: phase === 'typingTitle',
    subtitleActive: phase === 'typingSubtitle',
  };
};

export const ProblemSectionSlideBrowser = ({
  className,
  pairs = DEFAULT_SLIDE_PAIRS,
  pairIndex = 0,
  dissolving = false,
  onTypingComplete,
  titlePlaceholder = 'Click to add title',
  subtitlePlaceholder = 'Click to add subtitle',
}: ProblemSectionSlideBrowserProps) => {
  const pair = pairs[pairIndex] ?? pairs[0];
  const { titleText, subtitleText, titleActive, subtitleActive } =
    useForwardTypewriter(pair, pairIndex, onTypingComplete);

  return (
    <div
      className={cn(styles.slideBrowser, className)}
      data-name="ProblemSectionSlideBrowser"
    >
      <img
        src="/landing/problem-section/browser.svg"
        alt=""
        className={styles.browserImage}
        aria-hidden="true"
      />
      <div
        className={cn(
          styles.slideText,
          styles.slideTitle,
          dissolving && styles.dissolving,
        )}
        aria-hidden="true"
      >
        {titleText.length === 0 ? (
          <span className={styles.placeholder}>{titlePlaceholder}</span>
        ) : (
          <>
            <span className={styles.typed}>{titleText}</span>
            {titleActive ? <span className={styles.caret} /> : null}
          </>
        )}
      </div>
      <div
        className={cn(
          styles.slideText,
          styles.slideSubtitle,
          dissolving && styles.dissolving,
        )}
        aria-hidden="true"
      >
        {subtitleText.length === 0 ? (
          <span className={styles.placeholder}>{subtitlePlaceholder}</span>
        ) : (
          <>
            <span className={styles.typed}>{subtitleText}</span>
            {subtitleActive ? <span className={styles.caret} /> : null}
          </>
        )}
      </div>
    </div>
  );
};

ProblemSectionSlideBrowser.displayName = 'ProblemSectionSlideBrowser';
